import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import Container from "./Container";
import { Heading, Subheading } from "./text";
import NoiseSphere from "./NoiseSphere";

type SectionProps = {
  className?: string;
};

const ORANGE_HEX = "#FF8000";
const ACCENT_WEAK = "rgba(255,128,0,0.2)";

type PilotItem = {
  key: string;
  text: string;
  side: "left" | "right";
  order: number; // vertical order from top to bottom
};

const pilotItems: PilotItem[] = [
  { key: "stay-same", side: "left", order: 0, text: "1. Your current coding programs stay the same." },
  { key: "onboarding", side: "right", order: 0, text: "2. White-glove onboarding & setup (in-person)." },
  { key: "ramp", side: "left", order: 1, text: "3. Phased ramp-up to production with audits & accuracy testing." },
  { key: "second-pass", side: "right", order: 1, text: "4. Population-wide second-pass chart review with your current workflows." },
  { key: "identify-surface", side: "left", order: 2, text: "5. Identify and surface undercoding, overcoding, and prospective signals." },
  { key: "evidence", side: "right", order: 2, text: "6. Deltas compiled with rationale & evidence snippets for your team to review." },
  { key: "governance", side: "left", order: 3, text: "7. Coding & Clinical Governance Committee meets to review metrics and edge cases." },
  { key: "pricing", side: "right", order: 3, text: "8. Pricing based on % winnings." },
];

const Pilot = ({ className }: SectionProps) => {
  const [selectedKey, setSelectedKey] = useState<string>(pilotItems[0].key);
  const [progress, setProgress] = useState<number>(0); // 0..1 animation between steps
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);

  const leftList = useMemo(() => pilotItems.filter((i) => i.side === "left"), []);
  const rightList = useMemo(() => pilotItems.filter((i) => i.side === "right"), []);
  const steps = useMemo(
    () =>
      pilotItems
        .slice()
        .sort((a, b) => (a.order - b.order) || (a.side === "left" ? -1 : 1)),
    []
  );
  const selectedIndex =
    steps.findIndex((s) => s.key === selectedKey) >= 0
      ? steps.findIndex((s) => s.key === selectedKey)
      : 0;

  // Track breakpoints for responsive behavior
  const [isMdUp, setIsMdUp] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia && window.matchMedia("(min-width: 768px)").matches;
  });
  const [isLgUp, setIsLgUp] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia && window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsMdUp(e.matches);
    // Initial
    setIsMdUp(mq.matches);
    // Listen
    mq.addEventListener?.("change", handler as any);
    return () => {
      mq.removeEventListener?.("change", handler as any);
    };
  }, []);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsLgUp(e.matches);
    setIsLgUp(mq.matches);
    mq.addEventListener?.("change", handler as any);
    return () => {
      mq.removeEventListener?.("change", handler as any);
    };
  }, []);

  // Vertical carousel geometry (mirrors BestInClass medium layout), responsive to md+
  const GAP_PX = 24; // gap-6
  const CARD_H = isMdUp ? 120 : 200;
  const STEP_PX = CARD_H + GAP_PX;
  // Viewport height:
  // - default: half(prev) + current + half(next)
  // - >= lg: expand to show prev + current + next fully to reveal more neighbors
  const VIEW_H = isLgUp ? (CARD_H * 3 + 2 * GAP_PX) : (CARD_H * 2 + 2 * GAP_PX);
  const BASE_Y = (VIEW_H / 2 - CARD_H / 2) - (2 * STEP_PX);
  const SLIDE_MS = 320;
  const [isSliding, setIsSliding] = useState(false);
  const [slideDir, setSlideDir] = useState<"next" | "prev" | null>(null);
  const [animShift, setAnimShift] = useState(0);
  const goToIndex = (idx: number) => {
    const n = steps.length;
    const clamped = ((idx % n) + n) % n;
    setSelectedKey(steps[clamped].key);
    setProgress(0);
    startRef.current = performance.now();
  };
  const slideTo = (dir: "next" | "prev", onAfter?: () => void) => {
    if (isSliding) return;
    setSlideDir(dir);
    setIsSliding(true);
    setAnimShift(dir === "next" ? -STEP_PX : STEP_PX);
    window.setTimeout(() => {
      setIsSliding(false);
      setSlideDir(null);
      setAnimShift(0);
      onAfter?.();
    }, SLIDE_MS);
  };
  const next = () => {
    const idx = selectedIndex < 0 ? 0 : selectedIndex;
    slideTo("next", () => goToIndex(idx + 1));
  };
  const prev = () => {
    const idx = selectedIndex < 0 ? 0 : selectedIndex;
    slideTo("prev", () => goToIndex(idx - 1));
  };

  // Compute current and next positions (as percentages of the timeline height)
  const nextIndex = steps.length ? (selectedIndex + 1) % steps.length : 0;
  const currPct =
    steps.length > 1 ? (selectedIndex / (steps.length - 1)) * 100 : 0;
  const nextPct =
    steps.length > 1 ? (nextIndex / (steps.length - 1)) * 100 : 0;
  // Handover slightly before full progress to sync dot color and segment
  const HANDOVER = 0.8;
  const EPS = 0.05;
  const segTop = Math.min(currPct, nextPct);
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const isWrapping = steps.length > 0 && selectedIndex === steps.length - 1 && nextIndex === 0;
  const segActive = clampedProgress < HANDOVER && !isWrapping;
  const segHeight = segActive
    ? Math.abs(nextPct - currPct) * (clampedProgress / HANDOVER)
    : 0;

  const selectedOrder = pilotItems.find((i) => i.key === selectedKey)?.order ?? 0;

  const renderList = (list: PilotItem[]) => (
    <div className="space-y-10">
      {list.map((i) => {
        const isSelected = i.key === selectedKey;
        return (
          <button
            key={i.key}
            onClick={() => {
              setSelectedKey(i.key);
              setProgress(0);
              startRef.current = performance.now();
            }}
            className="group text-left cursor-pointer block"
            aria-selected={isSelected}
          >
            <p
              className="text-base sm:text-lg font-light text-white/80 group-hover:text-[#FF8000] max-w-md"
              style={isSelected ? { color: ORANGE_HEX } : undefined}
            >
              {i.text}
            </p>
          </button>
        );
      })}
    </div>
  );

  // Drive the auto-advance and progress animation
  useEffect(() => {
    const durationMs = 5000;
    startRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const p = Math.min(1, elapsed / durationMs);
      setProgress(p);
      if (p >= 1 && !isSliding) {
        const nextIdx = selectedIndex >= steps.length - 1 ? 0 : selectedIndex + 1;
        slideTo("next", () => {
          setSelectedKey(steps[nextIdx].key);
          startRef.current = performance.now();
          setProgress(0);
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [selectedIndex, steps, isSliding]);

  return (
    <section id="pilot" className={clsx("py-24", className)}>
      <Container>
        <div className="text-sm sm:text-sm font-base text-gray-400">PILOT</div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-white drop-shadow-lg">
          Pilot offering (limited):<br />Minimal disruption, upside-only guaranteed
        </h2>

        {/* Large layout (xl+): keep existing implementation intact */}
        <div className="relative mt-24 hidden xl:block">
          {/* Graph paper layer that bleeds a bit into side columns */}
          <div className="absolute inset-x-0 top-0 mx-48 md:mx-96 z-0">
            <div className="relative h-[360px] md:h-[420px]">
              <div
                className="absolute inset-0 rounded-sm pointer-events-none"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                  backgroundPosition: "0 0",
                }}
              />
              {/* Fade edges */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(ellipse at center, rgba(12,16,28,0) 62%, rgba(12,16,28,1) 92%), linear-gradient(to right, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 12%, rgba(12,16,28,0) 88%, rgba(12,16,28,1) 100%), linear-gradient(to bottom, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 12%, rgba(12,16,28,0) 88%, rgba(12,16,28,1) 100%)",
                }}
              />
            </div>
          </div>

          {/* Foreground content: per-row layout aligns texts with dots */}
          <div className="relative z-10 h-[300px] md:h-[360px]">
            {/* Global timeline line (full height of wrapper) */}
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-white/80" />
            {/* Animated progress segment between current and next dot */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-px z-20 pointer-events-none"
              style={{
                top: `${segTop}%`,
                height: `${segHeight}%`,
                backgroundColor: ORANGE_HEX,
              }}
            />

            {/* Orb centered over the whole group */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative h-[220px] w-[220px] sm:h-[260px] sm:w-[260px]">
                <NoiseSphere version={0} demo_is_active={false} />
              </div>
            </div>

            {/* Equidistant steps anchored by percent-based positions */}
            {steps.map((step, idx) => {
              const isLeft = step.side === "left";
              const isCurrent = idx === selectedIndex;
              const isNext = idx === nextIndex && progress >= HANDOVER - EPS;
              const active = (isCurrent && progress < HANDOVER) || isNext;
              const topPct =
                steps.length > 1 ? (idx / (steps.length - 1)) * 100 : 0;
              return (
                <div
                  key={step.key}
                  className="absolute left-0 right-0"
                  style={{ top: `${topPct}%`, transform: "translateY(-50%)" }}
                >
                  <div className="grid grid-cols-3 gap-x-24 items-center">
                    {/* Left cell */}
                    <div className="pr-4">
                      {isLeft ? (
                        <button
                          onClick={() => setSelectedKey(step.key)}
                          className="group text-left cursor-pointer block"
                          aria-selected={active}
                        >
                          <p
                            className="text-base sm:text-lg font-light text-white/80 group-hover:text-[#FF8000] max-w-md"
                            style={active ? { color: ORANGE_HEX } : undefined}
                          >
                            {step.text}
                          </p>
                        </button>
                      ) : (
                        <div />
                      )}
                    </div>
                    {/* Center dot cell */}
                    <div className="flex items-center justify-center">
                      <button
                        onClick={() => setSelectedKey(step.key)}
                        className="relative z-30 h-2 w-2 rounded-full border"
                        style={{
                          backgroundColor: active ? ORANGE_HEX : "#FFFFFF",
                          borderColor: active ? ORANGE_HEX : "#FFFFFF",
                        }}
                        aria-label={`Step ${idx + 1}`}
                      />
                    </div>
                    {/* Right cell */}
                    <div className="pl-4">
                      {!isLeft ? (
                        <button
                          onClick={() => setSelectedKey(step.key)}
                          className="group text-left cursor-pointer block"
                          aria-selected={active}
                        >
                          <p
                            className="text-base sm:text-lg font-light text-white/80 group-hover:text-[#FF8000] max-w-md"
                            style={active ? { color: ORANGE_HEX } : undefined}
                          >
                            {step.text}
                          </p>
                        </button>
                      ) : (
                        <div />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Small layout (<xl): left orb+timeline; right vertical carousel (side-by-side even on xs) */}
        <div className="relative mt-16 xl:hidden">
          <div className="flex flex-row items-stretch gap-6 md:gap-10">
            {/* Left column: compact timeline with orb */}
            <div className="relative shrink-0 w-[160px] sm:w-[200px]">
              <div className="relative" style={{ height: `${VIEW_H}px` }}>
                {/* Vertical line */}
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-white/80" />
                {/* Animated progress segment */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 w-px z-20 pointer-events-none"
                  style={{
                    top: `${segTop}%`,
                    height: `${segHeight}%`,
                    backgroundColor: ORANGE_HEX,
                  }}
                />
                {/* Orb */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative h-[140px] w-[140px] sm:h-[180px] sm:w-[180px]">
                    <NoiseSphere version={0} demo_is_active={false} />
                  </div>
                </div>
                {/* Dots aligned to timeline */}
                {steps.map((step, idx) => {
                  const isCurrent = idx === selectedIndex;
                  const isNextDot = idx === nextIndex && progress >= HANDOVER - EPS;
                  const active = (isCurrent && progress < HANDOVER) || isNextDot;
                  const topPct =
                    steps.length > 1 ? (idx / (steps.length - 1)) * 100 : 0;
                  return (
                    <div
                      key={`sm-dot-${step.key}`}
                      className="absolute left-1/2 -translate-x-1/2"
                      style={{ top: `${topPct}%`, transform: "translate(-50%, -50%)" }}
                    >
                      <button
                        onClick={() => setSelectedKey(step.key)}
                        className="relative z-30 h-2 w-2 rounded-full border"
                        style={{
                          backgroundColor: active ? ORANGE_HEX : "#FFFFFF",
                          borderColor: active ? ORANGE_HEX : "#FFFFFF",
                        }}
                        aria-label={`Step ${idx + 1}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Right column: vertical carousel (mirrors BestInClass medium layout) */}
            <div className="relative flex-1">
              <div className="relative overflow-hidden" style={{ height: `${VIEW_H}px` }}>
                {/* Top/Bottom fades */}
                <div className="pointer-events-none absolute inset-0 z-10">
                  <div
                    className="absolute inset-x-0 top-0"
                    style={{
                      height: "96px",
                      backgroundImage:
                        "linear-gradient(to bottom, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 100%)",
                    }}
                  />
                  <div
                    className="absolute inset-x-0 bottom-0"
                    style={{
                      height: "96px",
                      backgroundImage:
                        "linear-gradient(to top, rgba(12,16,28,1) 0%, rgba(12,16,28,0) 100%)",
                    }}
                  />
                </div>
                {(() => {
                  const n = steps.length || 1;
                  const i0 = ((selectedIndex - 2) % n + n) % n;
                  const i1 = ((selectedIndex - 1) % n + n) % n;
                  const i2 = selectedIndex >= 0 ? selectedIndex % n : 0;
                  const i3 = (i2 + 1) % n;
                  const i4 = (i2 + 2) % n;
                  const seq = [i0, i1, i2, i3, i4];
                  const stackStyle: React.CSSProperties = {
                    transform: `translateY(${BASE_Y + animShift}px)`,
                    transition: isSliding ? `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : undefined,
                    willChange: "transform",
                  };
                  return (
                    <div className="flex flex-col items-stretch gap-6" style={stackStyle}>
                      {seq.map((idxInSeq, k) => {
                        const it = steps[idxInSeq];
                        const isCurrent = k === 2;
                        if (!it) return null;
                        return (
                          <div key={`pilot-mid-${idxInSeq}-${k}`} className="w-full">
                            {isCurrent ? (
                              <div className="relative" style={{ height: `${CARD_H}px` }}>
                                {/* Floating progress bar */}
                                <div className="absolute top-0 left-0 right-0 h-px bg-white/20 z-20 pointer-events-none">
                                  <div
                                    className="h-px bg-[#FF8000] transition-none"
                                    style={{ width: `${progress * 100}%` }}
                                  />
                                </div>
                                <div className="rounded-none border p-5 overflow-hidden" style={{ borderColor: ACCENT_WEAK, height: `${CARD_H}px` }}>
                                  <div className="h-full flex flex-col items-start justify-center text-left">
                                    <div className="text-md lg:text-xl font-light" style={{ color: ORANGE_HEX }}>
                                      {it.text}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  if (k === 1) prev();
                                  else if (k === 3) next();
                                }}
                                className="block text-left w-full"
                              >
                                <div className="rounded-none border border-white/20 p-5 overflow-hidden" style={{ height: `${CARD_H}px` }}>
                                  <div className="h-full flex flex-col items-start justify-center text-left">
                                    <div className="text-md lg:text-xl font-light text-white">
                                      {it.text}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Pilot;


